import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, Globe, Search, ShoppingBag, Image as ImageIcon, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

interface AdAsset {
  id: number;
  asset_type: string;
  file_name: string;
  metadata?: any;
  r2_key?: string;
  content?: string;
}

interface AdPreviewProps {
  contentType: string;
  assets: AdAsset[];
  contentMetadata?: any;
}

export default function AdPreview({ contentType, assets, contentMetadata, selectedAsset }: AdPreviewProps & { selectedAsset?: AdAsset }) {
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile' | 'tablet'>('desktop');
  const [selectedAdGroup, setSelectedAdGroup] = useState(0);
  const [selectedAd, setSelectedAd] = useState(0);
  const [selectedCard, setSelectedCard] = useState(0);
  const [campaignData, setCampaignData] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlatform, setCurrentPlatform] = useState<string>('');

  // Parse JSON campaign specification - now based on selected asset
  useEffect(() => {
    // If we have a selected asset that's a JSON file, use it
    let specAsset = selectedAsset;
    let shouldUpdatePlatform = false;

    // Only look for a different asset if we have a selected asset that's a JSON file
    // or if we don't have a platform set yet
    if (selectedAsset?.file_name?.includes('.json')) {
      specAsset = selectedAsset;
      shouldUpdatePlatform = true;
    } else if (!currentPlatform && !campaignData && assets) {
      // Only find first JSON if we haven't loaded any campaign data yet
      specAsset = assets.find(a =>
        a.file_name?.includes('.json') ||
        a.metadata?.type === 'campaign_specification'
      );
      shouldUpdatePlatform = true;
    }

    if (specAsset?.content) {
      try {
        const data = typeof specAsset.content === 'string'
          ? JSON.parse(specAsset.content)
          : specAsset.content;

        // Only update campaign data if it's from a different file or first load
        if (!campaignData || specAsset.file_name !== campaignData._sourceFile) {
          setCampaignData({ ...data, _sourceFile: specAsset.file_name });
        }

        // Only determine platform if we should update it
        if (shouldUpdatePlatform && specAsset) {
          if (specAsset.file_name?.includes('google')) {
            setCurrentPlatform('google_ads');
          } else if (specAsset.file_name?.includes('facebook') || specAsset.file_name?.includes('instagram')) {
            setCurrentPlatform('facebook_ads');
          } else if (specAsset.file_name?.includes('linkedin')) {
            setCurrentPlatform('linkedin_ads');
          } else if (specAsset.file_name?.includes('display') || specAsset.file_name?.includes('ctv')) {
            setCurrentPlatform('display_ads');
          } else {
            setCurrentPlatform(contentType);
          }
        }
      } catch (e) {
        console.error('Failed to parse campaign data:', e);
      }
    }
  }, [selectedAsset?.id]); // Only re-run when selectedAsset ID changes

  // Get images for display
  const getImageAssets = () => (assets || []).filter(a => a.asset_type === 'image');
  const getVideoAssets = () => (assets || []).filter(a => a.asset_type === 'video');

  const renderGoogleAdsPreview = () => {
    if (!campaignData?.campaigns) return <div>Loading campaign data...</div>;

    const searchCampaign = campaignData.campaigns.find((c: any) => c.type === 'search');
    const displayCampaign = campaignData.campaigns.find((c: any) => c.type === 'display');
    const shoppingCampaign = campaignData.campaigns.find((c: any) => c.type === 'shopping');

    return (
      <div className="space-y-6">
        {/* Campaign Overview */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold text-lg mb-3">Campaign Overview</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Budget:</span>
              <p className="font-medium">{contentMetadata?.budget || '$5000/month'}</p>
            </div>
            <div>
              <span className="text-gray-500">Duration:</span>
              <p className="font-medium">{contentMetadata?.duration || '3 months'}</p>
            </div>
            <div>
              <span className="text-gray-500">Targeting:</span>
              <p className="font-medium">{contentMetadata?.targeting?.locations?.join(', ') || 'US, CA, UK'}</p>
            </div>
          </div>
        </div>

        {/* Search Ads Preview */}
        {searchCampaign && (
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Search Ads</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedAdGroup(Math.max(0, selectedAdGroup - 1))}
                  disabled={selectedAdGroup === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm px-2 py-1">
                  Ad Group {selectedAdGroup + 1} / {searchCampaign.ad_groups?.length || 1}
                </span>
                <button
                  onClick={() => setSelectedAdGroup(Math.min((searchCampaign.ad_groups?.length || 1) - 1, selectedAdGroup + 1))}
                  disabled={selectedAdGroup >= (searchCampaign.ad_groups?.length || 1) - 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {searchCampaign.ad_groups?.[selectedAdGroup] && (
              <div className="space-y-3">
                <div className="text-sm text-gray-600">
                  <strong>Ad Group:</strong> {searchCampaign.ad_groups[selectedAdGroup].name}
                </div>

                {searchCampaign.ad_groups[selectedAdGroup].ads?.map((ad: any, idx: number) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4">
                    {/* Google Search Result Preview */}
                    <div className="font-sans">
                      <div className="text-sm text-gray-600 mb-1">Ad · {ad.display_url || 'example.com'}</div>
                      <h3 className="text-blue-700 text-xl hover:underline cursor-pointer mb-1">
                        {ad.headlines?.[selectedAd % ad.headlines.length] || 'Headline'}
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {ad.descriptions?.[0] || 'Description line goes here'}
                      </p>
                      {/* Ad Extensions */}
                      <div className="mt-2 flex gap-3 text-sm">
                        <span className="text-blue-700 hover:underline cursor-pointer">Free Shipping</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-blue-700 hover:underline cursor-pointer">24/7 Support</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-blue-700 hover:underline cursor-pointer">Price Match</span>
                      </div>
                    </div>

                    {/* Headline Rotation */}
                    {ad.headlines?.length > 1 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="text-xs text-gray-500 mb-1">Headlines ({ad.headlines.length} variations):</div>
                        <div className="flex flex-wrap gap-1">
                          {ad.headlines.map((h: string, i: number) => (
                            <button
                              key={i}
                              onClick={() => setSelectedAd(i)}
                              className={`text-xs px-2 py-1 rounded ${
                                selectedAd === i ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Keywords */}
                <div className="text-sm">
                  <strong>Keywords:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {searchCampaign.ad_groups[selectedAdGroup].keywords?.map((kw: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Display Ads Preview */}
        {displayCampaign && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Display Ads</h3>
            <div className="grid grid-cols-2 gap-4">
              {getImageAssets().filter(a => a.metadata?.type === 'display_banner').map((banner, idx) => (
                <div key={banner.id} className="border rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-2">
                    {banner.metadata?.format || 'Banner'} ({banner.metadata?.size})
                  </div>
                  <div className="bg-gray-100 rounded flex items-center justify-center"
                       style={{ aspectRatio: banner.metadata?.size?.replace('x', '/') || '728/90' }}>
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shopping Ads */}
        {shoppingCampaign && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Shopping Ads</h3>
            <div className="grid grid-cols-3 gap-3">
              {shoppingCampaign.products?.map((product: any, idx: number) => (
                <div key={idx} className="border rounded-lg p-3">
                  <div className="bg-gray-100 aspect-square rounded mb-2 flex items-center justify-center">
                    <ShoppingBag className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-sm font-medium truncate">{product.title}</h4>
                  <p className="text-lg font-bold text-green-600">{product.price}</p>
                  <p className="text-xs text-gray-500">{product.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFacebookAdsPreview = () => {
    if (!campaignData?.campaigns) return <div>Loading campaign data...</div>;

    const campaign = campaignData.campaigns[0];
    const adSet = campaign?.ad_sets?.[0];
    const ads = adSet?.ads || [];

    const carouselAd = ads.find((a: any) => a.format === 'carousel');
    const videoAd = ads.find((a: any) => a.format === 'video');
    const storyAd = ads.find((a: any) => a.format === 'story');

    const deviceWidth = previewDevice === 'mobile' ? '375px' : previewDevice === 'tablet' ? '768px' : '500px';

    return (
      <div className="space-y-6">
        {/* Campaign Overview */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold text-lg mb-3">Campaign Overview</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Objective:</span>
              <p className="font-medium">{campaign?.objective || 'Conversions'}</p>
            </div>
            <div>
              <span className="text-gray-500">Budget:</span>
              <p className="font-medium">{contentMetadata?.budget || '$3000/month'}</p>
            </div>
            <div>
              <span className="text-gray-500">Audience:</span>
              <p className="font-medium">{contentMetadata?.audience_size || '2.5M people'}</p>
            </div>
          </div>
        </div>

        {/* Carousel Ad */}
        {carouselAd && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Carousel Ad</h3>
            <div className="mx-auto" style={{ maxWidth: deviceWidth }}>
              <div className="bg-white border rounded-lg overflow-hidden">
                {/* Facebook Header */}
                <div className="p-3 flex items-center space-x-2 border-b">
                  <div className="w-10 h-10 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Your Business</div>
                    <div className="text-xs text-gray-500">Sponsored · 🌐</div>
                  </div>
                </div>

                {/* Ad Text */}
                <div className="p-3">
                  <p className="text-sm">{carouselAd.primary_text}</p>
                </div>

                {/* Carousel */}
                <div className="relative bg-gray-100">
                  <div className="aspect-square flex items-center justify-center">
                    <div className="text-center">
                      <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm">Card {selectedCard + 1} of {carouselAd.cards?.length || 3}</p>
                    </div>
                  </div>

                  {/* Carousel Controls */}
                  <div className="absolute inset-y-0 left-2 flex items-center">
                    <button
                      onClick={() => setSelectedCard(Math.max(0, selectedCard - 1))}
                      className="p-1 bg-white rounded-full shadow-lg"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="absolute inset-y-0 right-2 flex items-center">
                    <button
                      onClick={() => setSelectedCard(Math.min((carouselAd.cards?.length || 3) - 1, selectedCard + 1))}
                      className="p-1 bg-white rounded-full shadow-lg"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Carousel Indicators */}
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                    {Array.from({ length: carouselAd.cards?.length || 3 }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${
                          i === selectedCard ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Card Details */}
                <div className="p-3 border-t">
                  <h4 className="font-semibold text-sm">{carouselAd.cards?.[selectedCard]?.headline}</h4>
                  <p className="text-xs text-gray-600">{carouselAd.cards?.[selectedCard]?.description}</p>
                  <button className="mt-2 w-full bg-blue-500 text-white py-2 rounded font-medium text-sm">
                    {carouselAd.call_to_action || 'Shop Now'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Ad */}
        {videoAd && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Video Ad</h3>
            <div className="mx-auto" style={{ maxWidth: deviceWidth }}>
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="p-3 flex items-center space-x-2 border-b">
                  <div className="w-10 h-10 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Your Business</div>
                    <div className="text-xs text-gray-500">Sponsored · 🎥</div>
                  </div>
                </div>

                <div className="p-3">
                  <p className="text-sm">{videoAd.primary_text}</p>
                </div>

                <div className="relative bg-black aspect-video flex items-center justify-center">
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="p-3 bg-white bg-opacity-80 rounded-full"
                  >
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>
                  <div className="absolute bottom-2 left-2 text-white text-xs">
                    0:00 / {getVideoAssets()[0]?.metadata?.duration || '0:15'}
                  </div>
                </div>

                <div className="p-3 border-t">
                  <h4 className="font-semibold text-sm">{videoAd.headline}</h4>
                  <button className="mt-2 w-full bg-blue-500 text-white py-2 rounded font-medium text-sm">
                    {videoAd.call_to_action || 'Learn More'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Story Ad */}
        {storyAd && previewDevice === 'mobile' && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Story Ad</h3>
            <div className="mx-auto" style={{ width: '250px' }}>
              <div className="bg-black rounded-2xl overflow-hidden" style={{ height: '445px' }}>
                <div className="h-full relative flex items-center justify-center">
                  <ImageIcon className="w-16 h-16 text-gray-400" />
                  <div className="absolute top-4 left-4 flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full"></div>
                    <span className="text-white text-sm font-semibold">Your Business</span>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 px-4">
                    <button className="w-full bg-white text-black py-3 rounded-full font-semibold text-sm">
                      Swipe Up
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLinkedInAdsPreview = () => {
    if (!campaignData?.campaigns) return <div>Loading campaign data...</div>;

    const campaign = campaignData.campaigns[0];
    const formats = campaign?.ad_formats || [];

    const sponsoredContent = formats.find((f: any) => f.type === 'sponsored_content');
    const messageAd = formats.find((f: any) => f.type === 'message_ad');
    const documentAd = formats.find((f: any) => f.type === 'document_ad');

    return (
      <div className="space-y-6">
        {/* Campaign Overview */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold text-lg mb-3">Campaign Overview</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Objective:</span>
              <p className="font-medium">{campaign?.objective || 'Lead Generation'}</p>
            </div>
            <div>
              <span className="text-gray-500">Targeting:</span>
              <p className="font-medium">
                {contentMetadata?.targeting?.job_titles?.slice(0, 2).join(', ') || 'C-Level Executives'}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Industries:</span>
              <p className="font-medium">
                {contentMetadata?.targeting?.industries?.join(', ') || 'Tech, Finance'}
              </p>
            </div>
          </div>
        </div>

        {/* Sponsored Content */}
        {sponsoredContent && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Sponsored Content</h3>
            <div className="mx-auto" style={{ maxWidth: '550px' }}>
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="p-4 flex items-start space-x-3">
                  <div className="w-12 h-12 bg-blue-700 rounded"></div>
                  <div className="flex-1">
                    <div className="font-semibold">Your Company</div>
                    <div className="text-xs text-gray-500">50,000 followers</div>
                    <div className="text-xs text-gray-500">Promoted</div>
                  </div>
                </div>

                <div className="px-4 pb-3">
                  <p className="text-sm">
                    Exciting insights for industry leaders. Our latest research reveals...
                  </p>
                </div>

                <div className="bg-gray-100 aspect-video flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-gray-400" />
                </div>

                <div className="p-4">
                  <h4 className="font-semibold">{sponsoredContent.creative?.headline}</h4>
                  <p className="text-sm text-gray-600 mt-1">{sponsoredContent.creative?.description}</p>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <div className="flex space-x-4 text-xs text-gray-500">
                      <span>👍 243</span>
                      <span>💬 18 comments</span>
                    </div>
                    <button className="text-blue-600 font-semibold text-sm">
                      {sponsoredContent.creative?.call_to_action || 'Download'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Ad */}
        {messageAd && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Sponsored InMail</h3>
            <div className="mx-auto bg-gray-50 rounded-lg p-4" style={{ maxWidth: '500px' }}>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="border-b pb-3 mb-3">
                  <div className="font-semibold text-sm">Subject: {messageAd.subject}</div>
                  <div className="text-xs text-gray-500 mt-1">From: Your Name at Your Company</div>
                </div>
                <div className="text-sm whitespace-pre-wrap">
                  {messageAd.template}
                </div>
                <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium">
                  Accept & Reply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Ad */}
        {documentAd && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Document Ad</h3>
            <div className="mx-auto" style={{ maxWidth: '400px' }}>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-red-100 rounded">
                    <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                      <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{documentAd.document?.title}</h4>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF · {documentAd.document?.total_pages} pages
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      Get instant access to exclusive insights
                    </p>
                    <button className="mt-3 w-full bg-blue-600 text-white py-2 rounded text-sm font-medium">
                      Download Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDisplayAdsPreview = () => {
    if (!campaignData?.campaigns) return <div>Loading campaign data...</div>;

    const campaign = campaignData.campaigns[0];
    const bannerSizes = ['728x90', '300x250', '160x600', '320x50'];

    return (
      <div className="space-y-6">
        {/* Campaign Overview */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold text-lg mb-3">Retargeting Campaign</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Type:</span>
              <p className="font-medium">{campaign?.type || 'Retargeting'}</p>
            </div>
            <div>
              <span className="text-gray-500">Budget:</span>
              <p className="font-medium">{contentMetadata?.budget || '$2000/month'}</p>
            </div>
            <div>
              <span className="text-gray-500">Networks:</span>
              <p className="font-medium">
                {contentMetadata?.networks?.join(', ') || 'GDN, Programmatic'}
              </p>
            </div>
          </div>
        </div>

        {/* Audience Segments */}
        {campaign?.audiences && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Audience Segments</h3>
            <div className="space-y-3">
              {campaign.audiences.map((audience: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium text-sm">{audience.name}</div>
                    <div className="text-xs text-gray-500">
                      Lookback: {audience.lookback_window} · Message: "{audience.creative_sets?.[0]?.message}"
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {audience.creative_sets?.[0]?.sizes?.length || 0} sizes
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Banner Previews */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold mb-4">Banner Sizes</h3>
          <div className="grid grid-cols-2 gap-4">
            {bannerSizes.map((size) => {
              const [width, height] = size.split('x').map(Number);
              const aspectRatio = `${width}/${height}`;
              const maxWidth = width > 400 ? 400 : width;
              const scale = maxWidth / width;

              return (
                <div key={size} className="border rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-2">
                    {size} - {
                      size === '728x90' ? 'Leaderboard' :
                      size === '300x250' ? 'Medium Rectangle' :
                      size === '160x600' ? 'Wide Skyscraper' :
                      'Mobile Banner'
                    }
                  </div>
                  <div
                    className="bg-gray-100 rounded flex items-center justify-center mx-auto"
                    style={{
                      width: `${width * scale}px`,
                      aspectRatio: aspectRatio
                    }}
                  >
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                      <div className="text-xs text-gray-500">Display Ad</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* HTML5 Creative */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold mb-4">Interactive HTML5 Ad</h3>
          <div className="mx-auto border rounded-lg bg-gray-100 p-8" style={{ width: '300px', height: '250px' }}>
            <div className="h-full flex flex-col items-center justify-center">
              <Globe className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600">Interactive Creative</p>
              <p className="text-xs text-gray-500 mt-1">Click to engage</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Device Preview Selector */}
      <div className="flex items-center justify-between p-4 pr-64 bg-white border-b">
        <div>
          <h2 className="font-semibold text-lg">
            {currentPlatform === 'google_ads' && 'Google Ads Campaign'}
            {currentPlatform === 'facebook_ads' && 'Facebook/Instagram Campaign'}
            {currentPlatform === 'linkedin_ads' && 'LinkedIn Campaign'}
            {currentPlatform === 'display_ads' && 'Display Network Campaign'}
            {!currentPlatform && 'Ad Campaign'}
          </h2>
          {selectedAsset && (
            <p className="text-xs text-gray-500 mt-1">
              Viewing: {selectedAsset.file_name}
            </p>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Device:</span>
          <button
            onClick={() => setPreviewDevice('desktop')}
            className={`p-2 rounded ${previewDevice === 'desktop' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            title="Desktop"
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPreviewDevice('tablet')}
            className={`p-2 rounded ${previewDevice === 'tablet' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            title="Tablet"
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPreviewDevice('mobile')}
            className={`p-2 rounded ${previewDevice === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            title="Mobile"
          >
            <Smartphone className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Ad Preview Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {currentPlatform === 'google_ads' && renderGoogleAdsPreview()}
          {currentPlatform === 'facebook_ads' && renderFacebookAdsPreview()}
          {currentPlatform === 'linkedin_ads' && renderLinkedInAdsPreview()}
          {currentPlatform === 'display_ads' && renderDisplayAdsPreview()}
          {!currentPlatform && (
            <div className="text-center text-gray-500 py-12">
              Select a campaign asset to view preview
            </div>
          )}
        </div>
      </div>

      {/* Asset Summary */}
      <div className="p-4 bg-white border-t">
        <div className="flex items-center justify-between text-sm">
          <div className="flex gap-4">
            <span className="text-gray-600">
              <strong>{(assets || []).length}</strong> total assets
            </span>
            <span className="text-gray-600">
              <strong>{getImageAssets().length}</strong> images
            </span>
            <span className="text-gray-600">
              <strong>{getVideoAssets().length}</strong> videos
            </span>
            <span className="text-gray-600">
              <strong>{(assets || []).filter(a => a.file_name?.includes('.json')).length}</strong> configs
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Last updated: {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  );
}